import { ReloadOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { Alert, App, Button, DatePicker, Input, Spin, Switch, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageTitle from 'components/page-title';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import ConfigCard from './components/config-card';
import ExclusionsCard from './components/exclusions-card';
import MilestonesTable from './components/milestones-table';
import SummaryTiles from './components/summary-tiles';
import { KcAdminData, KcConfig, KcRow, KcScope } from './types';
import {
  adminQuery,
  applyHidden,
  creatorName,
  currentMonthUtc,
  filterRows,
  formatDay,
  formatMonth,
  formatNumber,
  kcErrorMessage,
  milestoneOrderOf,
  normalizeAdminData,
  normalizeSyncResult,
  syncResultText,
} from './utils';
import styles from './style.module.scss';

const TITLE = 'Chúc mừng đạt mốc';

/** Dữ liệu kèm tháng đã hỏi: đổi tháng mà chưa có số mới thì không hiện số của tháng cũ. */
interface Loaded {
  requested: string;
  data: KcAdminData;
}

/**
 * Doanh thu → Chúc mừng đạt mốc (docs/chuc-mung-moc-kc/DAC-TA-2026-10-01.md §4.1, §6).
 * ADMIN và MOD xem, tính lại và ẩn / hiện từng Creator; khối cấu hình chỉ ADMIN sửa.
 */
const KcMilestonesPage = () => {
  const { message } = App.useApp();
  const { loaded: rolesLoaded, isAdmin, displayName } = useCurrentRoles();
  // '' = để máy chủ chọn: cùng tháng Trang chủ app đang hiện (đầu tháng chưa ai
  // đạt mốc → tháng trước). Có kết quả thì ô chọn tháng nhảy theo, không tải lại.
  const [month, setMonth] = useState<string>('');
  const autoLoaded = useRef<string | null>(null);
  const [state, setState] = useState<Loaded | null>(null);
  const [config, setConfig] = useState<KcConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  // honor (mặc định): chỉ Creator từ mốc vinh danh · all: cả Creator dưới mốc (để phân tích).
  const [scope, setScope] = useState<KcScope>('honor');
  // Tăng lên khi thêm / gỡ loại trừ từ bảng → thẻ Danh sách loại trừ tải lại.
  const [exclusionsKey, setExclusionsKey] = useState(0);

  const reload = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    if (month && autoLoaded.current === month) {
      autoLoaded.current = null;
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    AxiosUtils.get(API.kcMilestonesAdmin, adminQuery(month, scope))
      .then((res: any) => {
        if (cancelled) return;
        const data = normalizeAdminData(res?.data, month || currentMonthUtc());
        setState({ requested: data.month, data });
        setConfig(data.config);
        if (!month) {
          autoLoaded.current = data.month;
          setLoading(false);
          setMonth(data.month);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const text = kcErrorMessage(err);
        setLoadError(text);
        message.error(text);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, scope, refreshKey, message]);

  const view = state && state.requested === month ? state.data : null;
  const rows = useMemo(() => view?.rows || [], [view]);
  const filtered = useMemo(() => filterRows(rows, search), [rows, search]);
  const milestoneOrder = useMemo(() => milestoneOrderOf(view, config), [view, config]);
  // Tháng nào đã khép: theo máy chủ; chưa có số thì tự suy (tháng trước tháng hiện tại UTC).
  const closed = view ? view.closed : month < currentMonthUtc();

  const runSync = async () => {
    const target = view?.month || month;
    setSyncing(true);
    try {
      const res: any = await AxiosUtils.post(API.kcMilestonesAdminSync, { month: target });
      message.success(syncResultText(normalizeSyncResult(res?.data, target)));
      reload();
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const toggleHidden = async (row: KcRow, hidden: boolean) => {
    const target = view?.month || month;
    const uid = row.tiktokUid;
    setPending((prev) => ({ ...prev, [uid]: true }));
    try {
      await AxiosUtils.patch(API.kcMilestonesAdminCreators, { month: target, tiktokUid: uid, hidden });
      setState((prev) =>
        prev && prev.data.month === target
          ? { ...prev, data: applyHidden(prev.data, uid, hidden, displayName || null) }
          : prev
      );
      message.success(
        hidden
          ? `Đã ẩn ${creatorName(row.username, uid)} khỏi lời chúc mừng tháng ${formatMonth(target)}.`
          : `Đã hiện lại ${creatorName(row.username, uid)}.`
      );
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  // Thêm / gỡ khỏi danh sách loại trừ (mọi tháng, theo UID) ngay từ một dòng của bảng.
  const toggleExcluded = async (row: KcRow, excluded: boolean) => {
    const uid = row.tiktokUid;
    const name = creatorName(row.username, uid);
    setPending((prev) => ({ ...prev, [uid]: true }));
    try {
      if (excluded) {
        await AxiosUtils.post(API.kcMilestonesAdminExclusions, { query: uid });
      } else {
        await AxiosUtils._delete(
          `${API.kcMilestonesAdminExclusions}/${encodeURIComponent(uid)}`,
          undefined
        );
      }
      message.success(
        excluded ? `Đã loại trừ ${name} ở mọi tháng.` : `Đã gỡ ${name} khỏi danh sách loại trừ.`
      );
      reload();
      setExclusionsKey((key) => key + 1);
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  const onConfigSaved = (next: KcConfig, milestonesChanged: boolean) => {
    const honorChanged = !!config && next.honorMin !== config.honorMin;
    setConfig(next);
    // Đổi mốc: máy chủ đã tính lại tháng hiện tại; đổi mốc vinh danh: dòng nào được vinh danh đổi
    // theo → tải lại bảng.
    if (milestonesChanged || honorChanged) reload();
  };

  // Số Creator dưới mốc vinh danh: lấy ở lần tải gần nhất (không phụ thuộc scope đang xem).
  const belowCount = (view || state?.data)?.counts.below ?? 0;

  const keyword = search.trim();
  const emptyText = !view
    ? loadError
      ? 'Chưa tải được dữ liệu. Bấm Tải lại để thử lại.'
      : 'Đang tải dữ liệu…'
    : rows.length === 0
    ? !view.asOf
      ? 'Chưa có số liệu tháng này.'
      : scope === 'honor' && view.counts.below > 0
      ? `Chưa có Creator nào đạt mốc vinh danh trong tháng ${formatMonth(
          view.month
        )}. Bật “Hiện cả Creator dưới mốc vinh danh” để xem ${formatNumber(
          view.counts.below
        )} Creator dưới mốc.`
      : `Chưa có Creator nào đạt mốc trong tháng ${formatMonth(view.month)}.`
    : `Không có Creator nào khớp "${keyword}".`;

  return (
    <div>
      <PageTitle title={TITLE} />

      <ConfigCard config={config} canEdit={rolesLoaded && isAdmin} onSaved={onConfigSaved} />

      {config && !config.homeEnabled && (
        <Alert
          className={styles.section}
          type="warning"
          showIcon
          message="Đang tắt “Hiện trên Trang chủ app”: app không hiện lời chúc mừng cho ai."
        />
      )}

      <div className={styles.toolbar}>
        <DatePicker
          picker="month"
          format="MM/YYYY"
          allowClear={false}
          aria-label="Tháng"
          value={month ? dayjs(`${month}-01`) : null}
          disabledDate={(date) => date.format('YYYY-MM') > currentMonthUtc()}
          onChange={(date) => date && setMonth(date.format('YYYY-MM'))}
        />
        <Tooltip title="Tính lại sự kiện mốc của tháng này từ số liệu đã nhập. Không gửi push.">
          <Button icon={<SyncOutlined />} loading={syncing} disabled={loading} onClick={runSync}>
            Tính lại
          </Button>
        </Tooltip>
        <label className={styles.switchItem}>
          <Switch
            size="small"
            checked={scope === 'all'}
            onChange={(checked) => setScope(checked ? 'all' : 'honor')}
          />
          Hiện cả Creator dưới mốc vinh danh ({formatNumber(belowCount)})
        </label>
        {/* Thẻ div chứ không phải p: vòng chờ của antd là một thẻ div. */}
        <div className={styles.asOf} aria-live="polite">
          {!view ? (
            loading ? (
              <>
                <Spin size="small" /> Đang tải…
              </>
            ) : null
          ) : view.asOf ? (
            <span>
              Số liệu đến <strong className={styles.asOfDay}>{formatDay(view.asOf)}</strong>
            </span>
          ) : (
            <strong className={styles.asOfDay}>Chưa có số liệu tháng này</strong>
          )}
          {view && (
            <Tag color={view.closed ? 'default' : 'processing'}>
              {view.closed ? 'Tháng đã khép' : 'Tháng đang chạy'}
            </Tag>
          )}
        </div>
      </div>

      {loadError && (
        <Alert
          className={styles.section}
          type="error"
          showIcon
          message={view ? 'Chưa tải lại được. Đang hiện số của lần tải trước.' : 'Chưa tải được dữ liệu'}
          description={loadError}
          action={
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={reload}>
              Tải lại
            </Button>
          }
        />
      )}

      <SummaryTiles
        summary={view ? view.summary : null}
        closed={closed}
        milestoneOrder={milestoneOrder}
      />

      <div className={styles.tableBar}>
        <Input
          allowClear
          className={styles.search}
          prefix={<SearchOutlined />}
          placeholder="Tìm @TikTok ID hoặc nhóm"
          aria-label="Tìm Creator theo TikTok ID hoặc nhóm"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {view && (
          <span className={styles.count}>
            {keyword
              ? `${formatNumber(filtered.length)} / ${formatNumber(rows.length)} Creator`
              : `${formatNumber(rows.length)} Creator`}
          </span>
        )}
      </div>

      <MilestonesTable
        rows={filtered}
        loading={loading}
        milestoneOrder={milestoneOrder}
        pending={pending}
        onToggleHidden={toggleHidden}
        onToggleExcluded={toggleExcluded}
        emptyText={emptyText}
      />

      <ExclusionsCard refreshKey={exclusionsKey} onChanged={reload} />
    </div>
  );
};

export default KcMilestonesPage;
